import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { Check, ChevronDown, ChevronRight, ChevronsUpDown, FolderOpen, KeyRound, LogOut, PanelLeftClose, PanelLeftOpen, Plus,
  Settings, Upload, type LucideIcon } from "lucide-react";
import { LOCALES, getLocale, setLocale } from "../../i18n/index.js";
import { useDossier } from "../dossier";
import { PageBoundary } from "./boundary";
import { locale, w } from "../i18n";
import { api } from "../api/client";
import { useResource } from "../api/resource";
import { useCurrentUser } from "../auth";
import { ChangePasswordDialog } from "./password";
import { CommandPalette } from "./command";
import { Menu, MenuItem, MenuLabel, MenuSeparator, Tip, TipProvider } from "./menu";
import { NAV_GROUPS, initials, pagePath } from "../nav";
import { STATUS_GLYPH, STATUS_TONE, readinessLabel, readinessStatus } from "../status";

const SCOPE = ["A", "B", "C", "D", "E"];
const MOBILE = "(max-width: 700px)";
const COLLAPSED_KEY = "sfcr.sidebar.collapsed";

function LanguageSwitch() {
  return <div className="ws-seg" role="group" aria-label="Langue / Language">
    {LOCALES.map(locale => <button key={locale} type="button" className={getLocale() === locale ? "active" : ""}
      aria-pressed={getLocale() === locale} onClick={() => setLocale(locale)}>{locale.toUpperCase()}</button>)}
  </div>;
}

export function Header({ children }: { children?: ReactNode }) {
  return <header className="ws-header">
    <Link className="ws-header-brand" to="/"><img src="/seabird-tech-logo.svg" alt="SeaBird" />
      <span className="ws-header-product">{w("app.name")}</span></Link>
    {children}
    {!children && <div className="ws-header-spacer" />}
    <UserMenu />
    <LanguageSwitch />
  </header>;
}

/** The signed-in user and sign-out; nothing in local development mode (SFCR_AUTH_MODE=off). */
function UserMenu() {
  const me = useResource("auth:me", () => api.me());
  const [changing, setChanging] = useState(false);
  if (!me.data || me.data.mode !== "local") return null;
  const { user } = me.data;
  const signOut = async () => { try { await api.logout(); } finally { window.location.assign("/login"); } };
  return <>
    <Menu label={user.name} trigger={<button type="button" className="ws-avatar" aria-label={`${user.name} · ${w(`role.user.${user.role}`)}`}>
      {initials(user.name)}</button>}>
      <MenuLabel><strong>{user.name}</strong><span>{w(`role.user.${user.role}`)}</span></MenuLabel>
      <MenuSeparator />
      <MenuItem onSelect={() => setChanging(true)}><KeyRound size={16} aria-hidden="true" />{w("password.menu")}</MenuItem>
      <MenuItem onSelect={signOut}><LogOut size={16} aria-hidden="true" />{w("login.signOut")}</MenuItem>
    </Menu>
    {changing && <ChangePasswordDialog onClose={() => setChanging(false)} />}
  </>;
}

/** The current dossier, and every other one a click away. */
function DossierSwitcher() {
  const { dossier } = useDossier();
  const dossiers = useResource("dossiers", api.listDossiers);
  return <Menu label={w("nav.switchDossier")} align="start" trigger={
    <button type="button" className="ws-switcher" aria-label={`${w("nav.switchDossier")} · ${dossier.name} · ${dossier.entity_name}`}>
      <span className="ws-switcher-mark" aria-hidden="true">{initials(dossier.entity_name)}</span>
      <span className="ws-switcher-text"><strong>{dossier.name} · {dossier.entity_name}</strong>
        <span>{w("app.reportingYear", { year: dossier.reporting_year })} · {dossier.report_type}</span></span>
      <ChevronsUpDown size={14} aria-hidden="true" className="ws-switcher-chevron" />
    </button>}>
    <MenuLabel>{w("nav.dossiers")}</MenuLabel>
    {(dossiers.data || [dossier]).map(item => <MenuItem key={item.id} asChild>
      <Link to={`/dossiers/${item.id}`}>
        <span className="ws-switcher-mark sm" aria-hidden="true">{initials(item.entity_name)}</span>
        <span className="ws-menu-text"><strong>{item.name}</strong><span>{item.entity_name} · {item.reporting_year}</span></span>
        {item.id === dossier.id && <Check size={16} aria-label={w("nav.current")} />}
      </Link></MenuItem>)}
    <MenuSeparator />
    <MenuItem asChild><Link to="/?new=1"><Plus size={16} aria-hidden="true" />{w("nav.newDossier")}</Link></MenuItem>
    <MenuItem asChild><Link to="/?pick=1"><FolderOpen size={16} aria-hidden="true" />{w("nav.allDossiers")}</Link></MenuItem>
  </Menu>;
}

function DossierHeaderContext() {
  const { activeCount } = useDossier();
  return <>
    <DossierSwitcher />
    <div className="ws-header-spacer" />
    <CommandPalette />
    <div className={`ws-header-status ${activeCount > 0 ? "busy" : ""}`} role="status" aria-live="polite">
      {activeCount > 0
        ? <><span className="ws-status-glyph ws-spin ws-tone-info" aria-hidden="true">◐</span>{w("app.processing", { count: activeCount })}</>
        : <><span className="ws-dot" aria-hidden="true" />{w("app.allProcessed")}</>}
    </div>
  </>;
}

/** Whether the sidebar shows icons only: its labels then move into tooltips. */
const Collapsed = createContext(false);

function Item({ to, Icon, label, meta, end }: { to: string; Icon: LucideIcon; label: string; meta?: ReactNode; end?: boolean }) {
  const collapsed = useContext(Collapsed);
  // A string className, as the tooltip trigger would stringify a function one; NavLink adds "active" itself.
  return <Tip label={label} side="right" disabled={!collapsed}>
    <NavLink to={to} end={end} aria-label={collapsed ? label : undefined} className="ws-nav-item">
      <Icon size={16} aria-hidden="true" /><span className="ws-nav-label">{label}</span>{meta ? <span className="ws-nav-meta">{meta}</span> : null}
    </NavLink></Tip>;
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return <div className="ws-nav-group" role="group" aria-label={title}><div className="ws-nav-group-title">{title}</div>{children}</div>;
}

/** A chapter and its sections. Only the chapter being worked on starts open, so the menu stays short. */
function ChapterNav({ code, title, sections, base }: {
  code: string; title: string; base: string;
  sections: { code: string; title: string; status: "READY" | "PARTIALLY_READY" | "NOT_READY" }[];
}) {
  const collapsed = useContext(Collapsed);
  const { pathname } = useLocation();
  const inside = pathname.startsWith(`${base}/sections/${code}.`) || pathname === `${base}/chapters/${code}`;
  const [open, setOpen] = useState(inside);
  useEffect(() => { if (inside) setOpen(true); }, [inside]);
  const name = w("nav.chapterTitle", { code, title });
  const ready = sections.filter(section => section.status === "READY").length;
  return <div className="ws-nav-chapter">
    <div className="ws-nav-chapter-row">
      <button type="button" className="ws-icon-btn ws-nav-chevron" aria-expanded={open} onClick={() => setOpen(value => !value)}
        aria-label={name}>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</button>
      <Tip label={name} side="right" disabled={!collapsed}>
        <NavLink to={`${base}/chapters/${code}`} aria-label={name} title={collapsed ? undefined : name}
          className={`ws-nav-item ${inside && (!open || collapsed) ? "active" : ""}`}>
          <span className="ws-nav-letter" aria-hidden="true">{code}</span>
          <span className="ws-nav-label" aria-hidden="true">{title}</span>
          <span className="ws-nav-meta" aria-hidden="true">{ready}/{sections.length}</span>
        </NavLink></Tip>
    </div>
    {open && <ul className="ws-nav-children">{sections.map(section => {
      const status = readinessStatus(section.status);
      return <li key={section.code}>
        <NavLink to={`${base}/sections/${section.code}`} className={({ isActive }) => `ws-nav-item ${isActive ? "active" : ""}`}
          title={`${section.code} ${section.title} (${readinessLabel(section.status)})`}>
          <span className="ws-nav-code">{section.code}</span><span className="ws-nav-label">{section.title}</span>
          <span className={`ws-status ws-tone-${STATUS_TONE[status]}`} aria-label={readinessLabel(section.status)}>
            <span className="ws-status-glyph" aria-hidden="true">{STATUS_GLYPH[status]}</span></span>
        </NavLink></li>;
    })}</ul>}
  </div>;
}

function readCollapsed(): boolean {
  if (typeof window === "undefined") return false;
  if (window.matchMedia?.(MOBILE).matches) return true;
  try { return window.localStorage.getItem(COLLAPSED_KEY) === "1"; } catch { return false; }
}

export function Sidebar() {
  const { dossier, documents, readiness } = useDossier();
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  useEffect(() => {
    const media = window.matchMedia?.(MOBILE);
    if (!media) return;
    const change = (event: MediaQueryListEvent) => setCollapsed(event.matches || readCollapsed());
    const close = (event: KeyboardEvent) => { if (media.matches && event.key === "Escape") setCollapsed(true); };
    media.addEventListener("change", change);
    window.addEventListener("keydown", close);
    return () => { media.removeEventListener("change", change); window.removeEventListener("keydown", close); };
  }, []);
  useEffect(() => { if (window.matchMedia?.(MOBILE).matches) setCollapsed(true); }, [pathname]);
  const toggle = () => setCollapsed(value => {
    // The choice is remembered on wide screens; small screens always start collapsed.
    if (!window.matchMedia?.(MOBILE).matches) {
      try { window.localStorage.setItem(COLLAPSED_KEY, value ? "0" : "1"); } catch { /* storage unavailable */ }
    }
    return !value;
  });
  const base = `/dossiers/${dossier.id}`;
  const chapters = (readiness.data?.chapters || []).filter(chapter => SCOPE.includes(chapter.code));
  const attention = (documents.data || []).filter(d => d.status === "needs_review" || d.status === "failed").length;
  const meta: Record<string, ReactNode> = { documents: documents.data?.length || "", issues: attention || "" };
  const { local, can } = useCurrentUser();

  return <Collapsed.Provider value={collapsed}>
    <nav className={`ws-sidebar ${collapsed ? "collapsed" : ""}`} aria-label={w("nav.dossier")}>
      <div className="ws-sidebar-scroll">
        {NAV_GROUPS.map(group => <Group key={group.title} title={w(group.title)}>
          {group.pages.filter(page => !page.adminOnly || (local && can("admin"))).map(page =>
            <Item key={page.path} to={pagePath(dossier.id, page.path)} Icon={page.Icon} label={w(page.label)} end={page.end} meta={meta[page.path]} />)}
          {group.title === "nav.sfcr" && chapters.map(chapter => <ChapterNav key={chapter.code} code={chapter.code} title={chapter.title}
            base={base} sections={chapter.sections} />)}
          {group.title === "nav.output" && <Tip label={w("nav.exportHint")} side="right">
            <a className="ws-nav-item" href={api.reportExportUrl(dossier.id, locale())} download aria-label={collapsed ? w("nav.export") : undefined}>
              <Upload size={16} aria-hidden="true" /><span className="ws-nav-label">{w("nav.export")}</span><span className="ws-nav-meta">Word</span>
            </a></Tip>}
          {group.title === "nav.system" && <Item to="/admin" Icon={Settings} label={w("nav.admin")} />}
        </Group>)}
      </div>
      <button type="button" className="ws-nav-item ws-nav-toggle" onClick={toggle} aria-expanded={!collapsed}
        aria-label={collapsed ? w("nav.expand") : w("nav.collapse")}>
        {collapsed ? <PanelLeftOpen size={16} aria-hidden="true" /> : <PanelLeftClose size={16} aria-hidden="true" />}
        <span className="ws-nav-label">{w("nav.collapse")}</span>
      </button>
    </nav>
  </Collapsed.Provider>;
}

export function DossierShell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return <TipProvider delayDuration={300}><div className="ws-app">
    <a className="ws-skip" href="#ws-main">Aller au contenu</a>
    <Header><DossierHeaderContext /></Header>
    <div className="ws-body"><Sidebar /><main className="ws-main" id="ws-main"><div className="ws-main-inner"><PageBoundary resetKey={pathname}>{children}</PageBoundary></div></main></div>
    <Toaster position="bottom-right" className="ws-toaster" />
  </div></TipProvider>;
}
